import React, {
  type HTMLAttributes,
  useState,
  useRef,
  useCallback,
} from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { View } from '@uncinc/react-drupal-core';
import { viewGet } from '@uncinc/react-drupal-core/dist/redux/actions/Entity/View';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import type { FormForumCommentHelpers, FormForumCommentValues } from '../../../components/Forms/Forum/Comment';
import useToast from '../../../hooks/useToast';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { commentAdd } from '../../../store/actions/Entity/Comment';
import { nodeEdit } from '../../../store/actions/Entity/Node';
import type { ImmutableForumNodeEntity } from '..';
import {
  Breadcrumbs,
  ForumCommentForm,
  ForumPost,
  Button,
  Main,
  Popup,
  PopupCreateNickname,
} from '../../../components';
import usePopup from '../../../hooks/usePopup';

import './style.scss';

interface Props extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableForumNodeEntity;
}

const emptyInitialValues = {
  body: '',
  parentCommentId: null,
  parentAuthorName: '',
};

const NodeForum: React.FC<Props> = ({ entity }) => {
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const toast = useToast();
  const history = useHistory();

  const scrollRef = useRef<HTMLDivElement>(null);
  const popup = usePopup('createNicknamePopup', {
    closeOnEscape: true,
  });

  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));
  const nickname = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', uid, 'field_nickname', 0, 'value'], ''));
  const reactionCount = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['view', 'forum_post_comments', 'default', 'pager', 'total_items'], 0));

  // Calculates the page for the next comment, so that when a user
  // posts a new comment, we can make the pager go to the page with that
  // new comment.
  const nextPage = useSelector<ImmutableRootState<number>, number>(
    (state) => {
      const total = state.getIn(['view', 'forum_post_comments', 'default', 'pager', 'total_items'], 0);
      const itemsPerPage = state.getIn(['view', 'forum_post_comments', 'default', 'pager', 'items_per_page'], 10);
      const next = Math.ceil((total + 1) / itemsPerPage);
      return next;
    },
  );

  const nid = entity.getIn(['nid', 0, 'value']);
  const title = entity.getIn(['title', 0, 'value']);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const avatar = entity.getIn(['uid', 0, 'entity', 'field_avatar', 0, 'value'], 1);
  const isLiked = entity.getIn(['flags', 'like_post'], Immutable.List()).reduce((acc, flag) => {
    if (!acc) {
      return flag.getIn(['uid', 0, 'target_id']) === uid;
    }

    return acc;
  }, false);

  const isInappropriate = entity.getIn(['flags', 'inappropriate_post'], Immutable.List()).reduce((acc, flag) => {
    if (!acc) {
      return flag.getIn(['uid', 0, 'target_id']) === uid;
    }

    return acc;
  }, false);

  const [initialValues, setInitialValues] = useState(emptyInitialValues);

  const scrollToForm = (parentCommentId?: number, parentAuthorName?: string) => {
    setInitialValues((prevInitialValues) => ({
      ...prevInitialValues,
      parentCommentId,
      parentAuthorName,
    }));

    if (scrollRef.current !== null) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });

      if (document.querySelector<HTMLTextAreaElement>('textarea')) {
        document.querySelector<HTMLTextAreaElement>('textarea').focus();
      }
    }
  };

  const handleAddComment = useCallback(async (
    values: FormForumCommentValues,
    formProps: FormForumCommentHelpers,
  ) => {
    const comment = {
      entity_id: [{ target_id: nid }],
      entity_type: [{ value: 'node' }],
      comment_type: [{ target_id: 'comment_forum' }],
      field_name: [{ value: 'comment_forum' }],
      field_body: [{ value: values.body }],
      pid: [{ target_id: values.parentCommentId }],
    };

    try {
      // Wait for the node to be added.
      await dispatch(commentAdd(comment));

      // Go to the page where the comment will appear, and post the comment.
      history.push(`${history.location.pathname}?page=${nextPage}`);

      // Reset the form.
      const { resetForm } = formProps;
      resetForm({ values: emptyInitialValues });
      setInitialValues(emptyInitialValues);

      // Show success toast.
      toast.show(t('pages.forum_comment_create.success_message'), 'success');
    } catch (error) {
      if (error) {
        toast.show(t('pages.forum_comment_create.error_message'), 'error');
      }
    }
  }, [dispatch, history, nextPage, nid, t, toast]);

  const handleEditPost = useCallback(async (values: FormForumCommentValues) => {
    const forumPost = {
      type: [{ target_id: 'forum' }],
      body: [{ value: values.body }],
    };

    try {
      // Wait for the node to be edited.
      await dispatch(nodeEdit(nid, forumPost));

      // Show success toast.
      toast.show(t('pages.forum_post_edit.success_message'), 'success');
    } catch (error) {
      if (error) {
        toast.show(t('pages.forum_post_edit.error_message'), 'error');
      }
    }
  }, [dispatch, nid, t, toast]);

  return (
    <Main className="NodeForum col-12 col-lg-10 py-8 py-lg-12">
      {popup.isOpen && (
        <Popup
          {...popup.popupProps}
          title={t('pages.forum_nickname.title')}
          showCloseButton={false}
        >
          <PopupCreateNickname
            onClose={popup.close}
            onNicknameCreated={() => {
              popup.close();

              setTimeout(() => {
                // wait a little for the form to be rendered
                scrollToForm();
              }, 100);
            }}
          />
        </Popup>
      )}
      <div className="row">
        <div className="Page__introduction col-12">
          <Breadcrumbs />

          <h1 className="u-word-break">
            {title}
          </h1>

          <p>
            {t('pages.forum_post_header.response_count_text', { count: reactionCount })}
          </p>

          <Button
            type="submit"
            onClick={() => {
              if (!nickname) {
                popup.open();
                return;
              }

              scrollToForm();
            }}
          >
            {t('pages.forum.comment_form.button')}
          </Button>
        </div>

        <div className="Page__content col-12">
          <ForumPost
            id={nid}
            type="node"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            author={entity.getIn(['uid', 0, 'entity', 'name', 0, 'value'], '')}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            avatar={avatar}
            publicationDate={new Date(entity.getIn(['created', 0, 'value']))}
            updatedDate={new Date(entity.getIn(['changed', 0, 'value']))}
            messageProcessed={entity.getIn(['body', 0, 'processed'], '')}
            message={entity.getIn(['body', 0, 'value'], '')}
            isLiked={isLiked}
            likeCount={entity.getIn(['flags', 'like'], Immutable.List()).size}
            isInappropriate={isInappropriate}
            isOriginalPoster
            isMyPost={uid === entity.getIn(['uid', 0, 'target_id'])}
            isFirstPost
            hasCreatedNickname={!!nickname}
            onClickReply={scrollToForm}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onEditPost={handleEditPost}
          />

          <View
            id="forum_post_comments"
            display="default"
            args={[nid]}
            resetStateOnUnmount
            displayInitialLoadingState
            originalPosterId={entity.getIn(['uid', 0, 'target_id'])}
            onClickReply={scrollToForm}
            storageType="url"
            withProvider
          />

          {nickname ? (
            <ForumCommentForm
              className="mt-8"
              postTitle={title}
              initialValues={initialValues}
              scrollRef={scrollRef}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={handleAddComment}
              onDeleteMention={() => {
                setInitialValues((prevInitialValues) => ({
                  ...prevInitialValues,
                  parentCommentId: null,
                  parentAuthorName: '',
                }));
              }}
            />
          ) : (
            <>
              <h2 className="asH4 mt-8 mb-3">
                {t('pages.forum.comment_form.title', { title })}
              </h2>

              <p className="mb-4">
                <strong>
                  {t('pages.forum.comment_form.create_nickname')}
                </strong>
              </p>
              <Button
                type="submit"
                onClick={() => {
                  popup.open();
                }}
              >
                {t('pages.forum.comment_form.button')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Main>
  );
};

export default NodeForum;
